import { useState, useEffect } from 'react';

const TIMEZONE_OPTIONS = [
    { value: 0, label: 'Select Timezone', disabled: true },
    { value: -4, label: 'Atlantic Standard Time (GMT -04:00)' },
    { value: -5, label: 'Eastern Standard Time (GMT -05:00)' },
    { value: -6, label: 'Central Standard Time (GMT -06:00)' },
    { value: -7, label: 'Mountain Standard Time (GMT -07:00)' },
    { value: -8, label: 'Pacific Standard Time (GMT -08:00)' },
    { value: -9, label: 'Alaska Standard Time (GMT -09:00)' },
    { value: -10, label: 'Hawaii-Aleutian Standard Time (GMT -10:00)' },
];

const TONE_OPTIONS = [
    { value: '', label: 'Select Alert Tone', disabled: true },
    { value: 'Default', label: 'Default (iOS default)' },
    { value: 'Alarm', label: 'Alarm' },
    { value: 'Siren', label: 'Siren' },
    { value: 'Tones', label: 'Tones' },
    { value: 'Beeps', label: 'Beeps' },
    { value: 'Slide', label: 'Slide' },
    { value: 'Klaxon', label: 'Klaxon' },
];

const AppSettingsTab = ({ alertTone, campusTimeZone, logOutTimeInterval, onSave, saving }) => {
    const [tone, setTone] = useState(alertTone || '');
    const [timezone, setTimezone] = useState(campusTimeZone ?? 0);
    const [logout, setLogout] = useState(logOutTimeInterval || 1);
    const [dirty, setDirty] = useState(false);

    // Sync props when parent data reloads (campus switch)
    useEffect(() => {
        setTone(alertTone || '');
        setTimezone(campusTimeZone ?? 0);
        setLogout(logOutTimeInterval || 1);
        setDirty(false);
    }, [alertTone, campusTimeZone, logOutTimeInterval]);

    // Range slider value badge position
    const sliderPercent = ((logout - 1) / (30 - 1)) * 100;

    const handleSave = () => {
        onSave({
            AlertTone: tone,
            CampusTimeZone: parseInt(timezone),
            LogOutTimeInterval: parseInt(logout),
        });
    };

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">App Settings</h3>
                {dirty ? (
                    <div className="pp-section__actions">
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="pp-section__body">
                {/* Timezone + Alert Tone in a 2-col row */}
                <div className="ss-field-row">
                    <div className="ss-field">
                        <label className="ss-field__label" htmlFor="ss-timezone">
                            Campus Timezone
                        </label>
                        <select
                            id="ss-timezone"
                            className="ss-field__select"
                            value={timezone}
                            onChange={(e) => {
                                setTimezone(e.target.value);
                                setDirty(true);
                            }}
                        >
                            {TIMEZONE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="ss-field">
                        <label className="ss-field__label" htmlFor="ss-tone">
                            Alert Tone
                        </label>
                        <select
                            id="ss-tone"
                            className="ss-field__select"
                            value={tone}
                            onChange={(e) => {
                                setTone(e.target.value);
                                setDirty(true);
                            }}
                        >
                            {TONE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Automatic Logout Timer — range slider */}
                <div className="ss-range" style={{ marginTop: 'var(--space-6)' }}>
                    <div className="ss-range__header">
                        <h4 className="ss-range__title">Automatic Logout Timer</h4>
                    </div>
                    <p className="ss-range__desc">
                        Timeout length for idle user to automatically logout.
                    </p>
                    <div className="ss-range__track-wrap">
                        <span
                            className="ss-range__value"
                            style={{ left: `calc(${sliderPercent}% + ${10 - sliderPercent * 0.175}px)` }}
                        >
                            {logout} Day{logout !== 1 ? 's' : ''}
                        </span>
                        <input
                            className="ss-range__input"
                            type="range"
                            min={1}
                            max={30}
                            step={1}
                            value={logout}
                            onChange={(e) => {
                                setLogout(parseInt(e.target.value));
                                setDirty(true);
                            }}
                        />
                    </div>
                    <div className="ss-range__labels">
                        <span>1 Day</span>
                        <span>30 Days</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppSettingsTab;
